"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import {
  applyTheme,
  getClientThemeFallback,
  toggleTheme,
  type ThemeMode,
} from "@/lib/theme";

export default function ThemeToggleButton({
  className = "btn btn-ghost theme-toggle",
}: {
  className?: string;
}) {
  const { data, status, update } = useSession();
  const [theme, setTheme] = useState<ThemeMode>("light");

  useEffect(() => {
    const initial = getClientThemeFallback();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- client-only fallback applied after hydration
    setTheme(initial);
    applyTheme(initial);
  }, []);

  useEffect(() => {
    if (status === "loading") return;
    const pref = data?.user?.themePreference;
    const resolved: ThemeMode =
      pref === "light" || pref === "dark" ? pref : getClientThemeFallback();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync displayed theme with the session preference
    setTheme((current) => (current === resolved ? current : resolved));
  }, [data?.user?.themePreference, status]);

  const handleToggle = async () => {
    const next = toggleTheme(theme);
    setTheme(next);
    if (data?.user) {
      try {
        await update({ themePreference: next });
      } catch (e) {
        console.error("Failed to update session theme", e);
      }
      try {
        await fetch("/api/settings", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ themePreference: next }),
        });
      } catch (e) {
        console.error("Failed to persist theme", e);
      }
    }
  };

  const nextTheme: ThemeMode = theme === "dark" ? "light" : "dark";
  // Same glyphs as health-tracker: half-moon / half-sun style
  const themeIcon = theme === "dark" ? "◐" : "◑";

  return (
    <button
      type="button"
      className={className}
      aria-label={`Switch to ${nextTheme} mode`}
      title={`Switch to ${nextTheme} mode`}
      onClick={handleToggle}
    >
      {themeIcon}
    </button>
  );
}
